"use client";

import type { ReactNode } from "react";
import type { ColumnDef, SortingFn } from "@tanstack/react-table";

import {
  getAdminTableStableSerial,
  type AdminTableSerialStrategy,
} from "@/components/admin/admin-table-utils";
import {
  DataTableNumberInputCell,
  type DataTableFieldSync,
} from "@/components/ui/data-table-editable-cell";
import {
  colWidth,
  dataTableMeta,
  type DataTableColWidth,
} from "@/components/ui/data-table-column-width";
import { dataTableSortHeader } from "@/components/ui/data-table-sortable-header";
import { formatDateTime } from "@/lib/datetime/display";
import { formatMoney } from "@/lib/format-money";

export type AdminTableDraggableSerialCellProps = {
  serial: number;
};

const DEFAULT_SERIAL_COL = colWidth(72);
const DEFAULT_SORT_COL = colWidth(105);
const DEFAULT_CREATED_AT_COL = colWidth(143);

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- row-type-agnostic table helper
export const adminTableRowIndexSortingFn: SortingFn<any> = (rowA, rowB) =>
  rowA.index - rowB.index;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- row-type-agnostic table helper
export const adminTableSortOrderSortingFn: SortingFn<any> = (
  rowA,
  rowB,
  columnId,
) => {
  const left = Number(rowA.getValue(columnId));
  const right = Number(rowB.getValue(columnId));

  if (left !== right) {
    return left - right;
  }

  return rowA.index - rowB.index;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- row-type-agnostic table helper
export const adminTableDateTimeSortingFn: SortingFn<any> = (
  rowA,
  rowB,
  columnId,
) => {
  const left = Date.parse(String(rowA.getValue(columnId) ?? ""));
  const right = Date.parse(String(rowB.getValue(columnId) ?? ""));
  const leftMs = Number.isFinite(left) ? left : 0;
  const rightMs = Number.isFinite(right) ? right : 0;

  if (leftMs !== rightMs) {
    return leftMs - rightMs;
  }

  return rowA.index - rowB.index;
};

export const adminTableNumericFieldSortingFn = adminTableSortOrderSortingFn;

export type AdminTableSerialColumnOptions<_T> = {
  colWidth?: DataTableColWidth;
  className?: string;

  strategy?: AdminTableSerialStrategy;

  draggable?: boolean;
  renderCell?: (props: AdminTableDraggableSerialCellProps) => ReactNode;
};

export function adminTableSerialColumn<T extends { serial?: number | null }>(
  options: AdminTableSerialColumnOptions<T> & { strategy: "field" },
): ColumnDef<T, unknown>;
export function adminTableSerialColumn<T>(
  options?: AdminTableSerialColumnOptions<T>,
): ColumnDef<T, unknown>;
export function adminTableSerialColumn<T extends { serial?: number | null }>({
  colWidth: serialColWidth = DEFAULT_SERIAL_COL,
  className = "px-3",
  strategy = "auto-stable",
  draggable = false,
  renderCell,
}: AdminTableSerialColumnOptions<T> = {}): ColumnDef<T, unknown> {
  if (strategy === "field") {
    return {
      id: "serial",
      accessorKey: "serial",
      sortingFn: adminTableNumericFieldSortingFn,
      header: dataTableSortHeader("序号"),
      meta: {
        ...dataTableMeta(serialColWidth, { className, noTruncate: true }),
        serialStrategy: "field" as const,
      },
      enableSorting: true,
      cell: ({ row }) => (
        <span className="tabular-nums">{row.original.serial ?? "—"}</span>
      ),
    };
  }

  if (draggable) {
    if (!renderCell) {
      throw new Error("adminTableSerialColumn: draggable requires renderCell");
    }

    return {
      id: "serial",
      accessorFn: (_row, index) => index,
      sortingFn: adminTableRowIndexSortingFn,
      header: dataTableSortHeader("序号"),
      meta: {
        ...dataTableMeta(serialColWidth, { className, noTruncate: true }),
        serialStrategy: strategy,
        customSerial: true,
        draggable: true,
      },
      enableSorting: true,
      cell: ({ row, table }) => {
        const coreRow = table
          .getCoreRowModel()
          .rows.find((candidate) => candidate.id === row.id);
        const stableIndex = coreRow?.index ?? row.index;

        return renderCell({
          serial: getAdminTableStableSerial(stableIndex),
        });
      },
    };
  }

  return {
    id: "serial",
    accessorFn: (_row, index) => index,
    sortingFn: adminTableRowIndexSortingFn,
    header: dataTableSortHeader("序号"),
    meta: {
      ...dataTableMeta(serialColWidth, { className, noTruncate: true }),
      serialStrategy: strategy,
    },
    enableSorting: true,
    cell: () => null,
  };
}

type AdminTableSortOrderRow = { id: string; sortOrder: number };

export function adminTableSortOrderColumn<T extends AdminTableSortOrderRow>({
  onFieldChange,
  colWidth: sortColWidth = DEFAULT_SORT_COL,
  className = "px-3",
  inputClassName = "w-full",
}: {
  onFieldChange?: DataTableFieldSync;
  colWidth?: DataTableColWidth;
  className?: string;
  inputClassName?: string;
}): ColumnDef<T, unknown> {
  return {
    id: "sortOrder",
    accessorKey: "sortOrder",
    sortingFn: adminTableSortOrderSortingFn,
    header: dataTableSortHeader("排序"),
    meta: dataTableMeta(sortColWidth, { className }),
    enableSorting: true,
    cell: ({ row }) => onFieldChange ? (
      <DataTableNumberInputCell
        rowId={row.original.id}
        field="sortOrder"
        value={row.original.sortOrder}
        className={inputClassName}
        onFieldChange={onFieldChange}
      />
    ) : row.original.sortOrder,
  };
}

export function adminTableCreatedAtColumn<T extends { createdAt: string }>({
  colWidth: createdAtColWidth = DEFAULT_CREATED_AT_COL,
  className,
  header = "时间",
}: {
  colWidth?: DataTableColWidth;
  className?: string;
  header?: string;
} = {}): ColumnDef<T, unknown> {
  return {
    id: "createdAt",
    accessorKey: "createdAt",
    sortingFn: adminTableDateTimeSortingFn,
    header: dataTableSortHeader(header),
    meta: dataTableMeta(createdAtColWidth, className ? { className } : undefined),
    enableSorting: true,
    cell: ({ row }) => formatDateTime(row.original.createdAt),
  };
}

export function renderAdminTableSignedMoney(delta: number) {
  if (delta > 0) {
    return (
      <span className="tabular-nums text-emerald-600">
        + ¥ {formatMoney(delta)}
      </span>
    );
  }

  if (delta < 0) {
    return (
      <span className="tabular-nums text-destructive">
        - ¥ {formatMoney(Math.abs(delta))}
      </span>
    );
  }

  return <span className="tabular-nums text-muted-foreground">¥ 0.00</span>;
}

export function renderAdminTableSignedPoints(delta: number) {
  if (delta > 0) {
    return <span className="tabular-nums text-emerald-600">+{delta}</span>;
  }

  if (delta < 0) {
    return <span className="tabular-nums text-destructive">{delta}</span>;
  }

  return <span className="tabular-nums text-muted-foreground">0</span>;
}
